import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, diagnoseNoResponse } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useDashboardMode } from '@/store/mode';

interface MemberStatus {
  tier: 'FREE' | 'MEMBER';
  interval?: 'month' | 'year' | null;
  expiresAt?: string | null;
  configured: boolean;
  voucherGBP?: number;
  voucherExpiresAt?: string | null;
  usage?: {
    ordersUsed: number; ordersLimit: number; ordersLeft: number;
    spentGBP: number; spendCapGBP: number; spendLeftGBP: number;
    feeFree: boolean; resetsOn: string;
  };
  prices: { monthly: number; annual: number };
}

const perks = [
  { icon: '💸', label: 'Fee-free shopping',       detail: '12 orders / £300 a month, then 3.5% + 60p' },
  { icon: '🎟️', label: '£4 delivery voucher',     detail: 'When you spend £300+ in a month' },
  { icon: '⚡', label: 'Early drop access',       detail: '1 hour before everyone else' },
  { icon: '✅', label: 'Verified buyer badge',    detail: 'Builds trust with sellers' },
  { icon: '🎯', label: 'Priority support',        detail: 'Real humans, fast responses' },
];

export default function DashboardMembership() {
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<'monthly' | 'annual' | 'cancel' | null>(null);
  const [showCancel, setShowCancel] = useState(false);

  const qc = useQueryClient();
  const { data, refetch } = useQuery<MemberStatus>({
    queryKey: ['membership'],
    queryFn: async () => (await api.get('/membership')).data,
    retry: false,
    // Always reflect the true server state - never a stale "FREE" snapshot.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
  const { data: wallet } = useQuery<{ buyer?: { creditGBP: number } }>({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get('/me/wallet')).data,
    retry: false,
  });
  const souqliCreditGBP = wallet?.buyer?.creditGBP ?? 0;

  /** Pull the latest status everywhere the tier is shown (product page, cart…). */
  async function syncMembership() {
    await refetch();
    qc.invalidateQueries({ queryKey: ['membership'] });
    qc.invalidateQueries({ queryKey: ['overview'] });
  }

  // Returned from Stripe Checkout.
  useEffect(() => {
    const m = params.get('membership');
    const sessionId = params.get('session_id');
    if (m === 'success') {
      (async () => {
        // Activate straight away from the Checkout session - this works even if
        // the Stripe webhook never reaches the server.
        if (sessionId) {
          try { await api.post('/membership/confirm', { sessionId }); } catch { /* fall back to polling */ }
        }
        toast.success('Welcome to Souqli Member! 🎉');
        await syncMembership();
        // Belt and braces: poll briefly in case activation came via webhook.
        [1500, 4000].forEach((ms) => setTimeout(syncMembership, ms));
      })();
    }
    if (m === 'cancelled') toast('Checkout cancelled.', { icon: 'ℹ️' });
    if (m) { params.delete('membership'); params.delete('session_id'); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActive = data?.tier === 'MEMBER';
  const expires = data?.expiresAt ? new Date(data.expiresAt) : null;
  const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / 86_400_000) : null;
  const expiringSoon = isActive && daysLeft !== null && daysLeft <= 7;
  const monthly = data?.prices.monthly ?? 10.99;
  const annual = data?.prices.annual ?? 118.69;
  const usage = data?.usage;
  const user = useAuthStore((s) => s.user);
  // Which view is active (Buying / Selling) - a display choice, not a
  // permission. Computed identically to DashboardLayout so this page always
  // agrees with the nav: default to SELLER only when no mode is stored for the
  // signed-in account. Membership is gated on this, NOT on user.role - an
  // account that has activated selling (role SELLER) must still see Membership
  // while it is in Buyer view.
  const storedMode = useDashboardMode((s) => s.mode);
  const storedUserId = useDashboardMode((s) => s.userId);
  const mode = (storedMode && storedUserId === user?.id) ? storedMode : 'SELLER';
  const isAnnual = data?.interval === 'year';

  async function subscribe(plan: 'monthly' | 'annual', useSouqliCredit = false) {
    setBusy(plan);
    try {
      const { data: res } = await api.post('/membership/checkout', { plan, useSouqliCredit });
      // Live Stripe: redirect to hosted Checkout. Demo (no keys): activated
      // server-side already - just refresh the status in place.
      if (res?.url) { window.location.href = res.url; return; }
      if (res?.demo || res?.activated) {
        toast.success(
          res?.paidWithCredit
            ? `Paid with your Souqli Credit - welcome to Souqli Member! 🎉`
            : plan === 'annual'
              ? 'Annual membership active! 🎉 You just saved on a full year.'
              : 'Welcome to Souqli Member! 🎉 Fee-free on up to 12 orders / £300 a month.',
        );
        // Flip straight to the active-member view without a manual reload.
        await syncMembership();
        return;
      }
      // Reached only if the API returned 200 with neither a url nor an
      // activation flag - a real bug rather than a payment problem.
      toast.error('The server accepted the request but sent nothing back. Check the backend terminal.', { duration: 9000 });
    } catch (e: any) {
      // No response at all: backend down, crashed, or blocked by CORS.
      if (!e?.response) {
        toast.error(await diagnoseNoResponse(), { duration: 9000 });
        return;
      }
      const { status, data } = e.response;

      // 502 carries Stripe's own wording, which names the actual fault.
      if (status === 502 && data?.message) {
        toast.error(data.message, { duration: 14000 });
        return;
      }
      // 503 = database unavailable / schema out of date.
      if (status === 503 && data?.message) {
        toast.error(data.message, { duration: 14000 });
        return;
      }
      if (status === 401) {
        toast.error('Your session expired. Log in again and retry.');
        return;
      }
      toast.error(data?.message ?? `Membership checkout failed (error ${status}).`, { duration: 9000 });
    } finally { setBusy(null); }
  }

  async function cancel() {
    setBusy('cancel');
    try {
      await api.post('/membership/cancel');
      toast.success('Membership cancelled.');
      setShowCancel(false);
      refetch();
    } catch {
      toast.error('Could not cancel. Try again.');
    } finally { setBusy(null); }
  }

  // Membership is a buyer-view benefit - the Selling view gets Grow your store
  // instead. Gate on the active view mode (not user.role) so an account that
  // sells can still open Membership while it is in Buyer view.
  if (mode === 'SELLER') {
    return (
      <div className="rounded-2xl border border-warmline bg-white p-8 text-center max-w-[520px] mx-auto mt-8">
        <div className="text-4xl mb-3">★</div>
        <h1 className="display text-xl text-ink">Membership is for buyers</h1>
        <p className="text-mute text-sm mt-2 leading-relaxed">
          Fee-free orders, delivery vouchers and early access apply to buyer accounts.
          Your seller tools live in Grow your store.
        </p>
        <a href="/dashboard/grow" className="btn btn-primary mt-6">Grow your store</a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="display text-2xl text-ink mb-1">Membership</h1>
        <p className="text-mute text-sm">
          {isActive
            ? 'Manage your Souqli Member subscription.'
            : 'Membership is for buying - fee-free orders, delivery vouchers and early access.'}
        </p>
      </div>


      {expiringSoon && (
        <div className="rounded-luxe border border-warn/40 bg-warn/10 p-4 text-sm">
          ⏰ Your membership {daysLeft && daysLeft > 0 ? `renews in ${daysLeft} day${daysLeft > 1 ? 's' : ''}` : 'expires today'}
          {data?.interval ? ` (${data.interval === 'year' ? 'annual' : 'monthly'} plan)` : ''}. It renews automatically - cancel any time below.
        </div>
      )}

      {/* Status / plans */}
      {isActive ? (
        <>
          {/* ── The member card (annual gets the premium treatment) ── */}
          <div className={`section-dark relative overflow-hidden rounded-2xl text-cream shadow-frame ${isAnnual ? 'ring-2 ring-gold/60' : ''}`}
               style={isAnnual
                 ? { background: 'linear-gradient(135deg,#1a1208 0%,#221A12 45%,#3a2a12 100%)' }
                 : { background: '#221A12' }}>
            <div className="absolute inset-0 mashrabiya opacity-[0.07] pointer-events-none" />
            <span className={`absolute -right-16 -top-20 w-72 h-72 rounded-full blur-3xl pointer-events-none ${isAnnual ? 'bg-gold/35' : 'bg-gold/20'}`} />
            <span className="absolute -left-10 -bottom-24 w-64 h-64 rounded-full bg-gold/10 blur-3xl pointer-events-none" />

            <div className="relative p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ok opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-ok" />
                    </span>
                    <span className="stamp text-ok text-[9px] tracking-[0.2em]">SUBSCRIPTION ACTIVE</span>
                    {isAnnual && (
                      <span className="rounded-full bg-gold px-2.5 py-0.5 text-[8px] font-bold tracking-widest text-ink">★ ANNUAL</span>
                    )}
                  </div>
                  <div className="display text-3xl sm:text-[34px] text-gold leading-none mt-3">
                    {isAnnual ? 'Souqli Member · Annual' : 'Souqli Member'}
                  </div>
                  {isAnnual && (
                    <p className="text-[12px] text-cream/60 mt-2">Top tier - you&rsquo;re on our best-value plan.</p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-xl grid place-items-center shadow-glow shrink-0"
                     style={{ background: 'linear-gradient(135deg,#E7A23E,#A66018)' }}>
                  <span className="display text-ink text-2xl font-bold leading-none">S</span>
                </div>
              </div>

              {/* card details row */}
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-y-5 gap-x-4">
                <div>
                  <div className="stamp text-cream/40 text-[8px]">MEMBER</div>
                  <div className="text-[15px] font-medium text-cream mt-1 truncate">{user?.name ?? 'You'}</div>
                </div>
                <div>
                  <div className="stamp text-cream/40 text-[8px]">PLAN</div>
                  <div className="text-[15px] font-medium text-cream mt-1">
                    {data?.interval === 'year' ? 'Annual' : 'Monthly'}
                  </div>
                </div>
                <div>
                  <div className="stamp text-cream/40 text-[8px]">{data?.interval === 'year' ? 'RENEWS' : 'NEXT BILLING'}</div>
                  <div className="text-[15px] font-medium text-cream mt-1">
                    {expires ? expires.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Automatic'}
                  </div>
                </div>
                <div>
                  <div className="stamp text-cream/40 text-[8px]">PAYING</div>
                  <div className="text-[15px] font-medium text-cream mt-1">
                    £{data?.interval === 'year' ? annual.toFixed(0) : monthly.toFixed(2)}
                    <span className="text-cream/45 text-[12px]">/{data?.interval === 'year' ? 'yr' : 'mo'}</span>
                  </div>
                </div>
              </div>

              <div className="mt-7 pt-5 border-t border-cream/10 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-cream/70">
                  <span>✓ Fee-free allowance</span>
                  <span>✓ 1-hour early access</span>
                  <span>✓ Verified badge</span>
                  <span>✓ Priority support</span>
                </div>
                <button onClick={() => setShowCancel(true)} className="text-[12px] text-cream/45 hover:text-bad underline underline-offset-2 shrink-0">
                  Cancel membership
                </button>
              </div>
            </div>
          </div>

          {/* This month's allowance */}
          {usage && (
            <div className="rounded-luxe border border-warmline bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-ink text-sm">This month&rsquo;s benefits</h2>
                <span className="text-[11px] text-mute">Resets {new Date(usage.resetsOn).toLocaleDateString()}</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                {/* Fee-free orders */}
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-[12px] text-mute">Fee-free orders</span>
                    <span className="text-[13px] text-ink font-semibold">{usage.ordersLeft} of {usage.ordersLimit} left</span>
                  </div>
                  <div className="h-2 rounded-full bg-cream-deep overflow-hidden">
                    <div className="h-full saffron-fill rounded-full transition-all"
                      style={{ width: `${Math.min(100, (usage.ordersUsed / usage.ordersLimit) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-mute mt-1.5">{usage.ordersUsed} used</p>
                </div>

                {/* Fee-free spend */}
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-[12px] text-mute">Fee-free spend</span>
                    <span className="text-[13px] text-ink font-semibold">£{usage.spendLeftGBP.toFixed(2)} left</span>
                  </div>
                  <div className="h-2 rounded-full bg-cream-deep overflow-hidden">
                    <div className="h-full saffron-fill rounded-full transition-all"
                      style={{ width: `${Math.min(100, (usage.spentGBP / usage.spendCapGBP) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-mute mt-1.5">£{usage.spentGBP.toFixed(2)} of £{usage.spendCapGBP.toFixed(0)} used</p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-warmline flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px]">
                <span className={usage.feeFree ? 'text-ok font-medium' : 'text-mute'}>
                  {usage.feeFree ? '✓ Buying fee-free right now' : 'Allowance used - orders now attract 3.5% + 60p'}
                </span>
                <span className="text-mute">
                  🎟️ £4 voucher: <span className="text-ink font-medium">£{(data?.voucherGBP ?? 0).toFixed(2)}</span> available
                  {data?.voucherExpiresAt && ` · expires ${new Date(data.voucherExpiresAt).toLocaleDateString()}`}
                </span>
              </div>
            </div>
          )}

          {/* Annual members: confirmation they're on the top plan */}
          {isAnnual && (
            <div className="relative overflow-hidden rounded-2xl border-2 border-gold bg-gradient-to-br from-gold/14 to-gold/4 p-6 flex flex-wrap items-center gap-5 justify-between">
              <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gold/25 blur-2xl pointer-events-none" />
              <div className="relative">
                <span className="inline-block rounded-full bg-gold px-2.5 py-1 text-[9px] font-bold tracking-widest text-ink mb-2.5">BEST PLAN ACTIVE</span>
                <h3 className="display text-xl text-ink leading-tight">You&rsquo;re on the annual plan 🎉</h3>
                <p className="text-[13px] text-mute mt-1.5 max-w-[54ch]">
                  You&rsquo;re saving £{(monthly * 12 - annual).toFixed(2)} a year versus monthly billing - nothing more to upgrade.
                  Your benefits renew automatically on {expires ? expires.toLocaleDateString() : 'your renewal date'}.
                </p>
              </div>
              <div className="relative text-right shrink-0">
                <div className="display text-3xl text-gold-dark leading-none">£{annual.toFixed(0)}</div>
                <div className="stamp text-mute text-[9px] mt-1.5">PER YEAR · ≈ £{(annual / 12).toFixed(2)}/MO</div>
              </div>
            </div>
          )}

          {/* Upgrade to annual */}
          {!isAnnual && (
            <div className="relative overflow-hidden rounded-2xl border-2 border-gold bg-gradient-to-br from-gold/12 to-gold/4 p-6">
              <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gold/20 blur-2xl pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                <div className="min-w-0">
                  <span className="inline-block rounded-full bg-gold px-2.5 py-1 text-[9px] font-bold tracking-widest text-ink mb-2.5">
                    SAVE £{(monthly * 12 - annual).toFixed(2)} A YEAR
                  </span>
                  <h3 className="display text-xl text-ink leading-tight">Move to the annual plan</h3>
                  <p className="text-[13px] text-mute mt-1.5 max-w-[52ch]">
                    Identical benefits, billed once instead of twelve times - that&rsquo;s{' '}
                    {Math.round(((monthly * 12 - annual) / (monthly * 12)) * 100)}% off and no monthly charges to think about.
                  </p>
                  <div className="flex items-baseline gap-3 mt-3">
                    <span className="text-[13px] text-mute line-through">£{(monthly * 12).toFixed(2)}/yr</span>
                    <span className="display text-2xl text-gold-dark leading-none">£{annual.toFixed(0)}<span className="text-[13px] text-mute">/yr</span></span>
                    <span className="text-[12px] text-sage-dark font-medium">≈ £{(annual / 12).toFixed(2)}/mo</span>
                  </div>
                </div>
                <button
                  onClick={() => subscribe('annual')}
                  disabled={busy !== null || data?.configured === false}
                  className="btn btn-gold btn-lg shrink-0 font-semibold"
                >
                  {busy === 'annual' ? 'Opening checkout…' : 'Upgrade to annual →'}
                </button>
              </div>
              <p className="relative text-[11px] text-mute mt-4">
                Your current monthly plan stays active until it is replaced - you will not be double-charged.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Monthly */}
          <div className="rounded-luxe border border-warmline bg-white p-6 flex flex-col">
            <div className="stamp text-gold-dark text-[9px]">MONTHLY</div>
            <div className="display text-4xl mt-2">£{monthly.toFixed(2)}<span className="text-base text-mute">/mo</span></div>
            <p className="text-xs text-mute mt-1">Flexible - cancel any time.</p>
            <button onClick={() => subscribe('monthly')} disabled={busy !== null || data?.configured === false} className="btn btn-outline w-full mt-4">
              {busy === 'monthly' ? 'Opening checkout…' : 'Choose monthly'}
            </button>
            {souqliCreditGBP >= monthly && (
              <button onClick={() => subscribe('monthly', true)} disabled={busy !== null} className="text-[12px] text-sage-dark hover:underline mt-2">
                Pay with your £{souqliCreditGBP.toFixed(2)} Souqli Credit instead
              </button>
            )}
          </div>
          {/* Annual */}
          <div className="rounded-luxe border-2 border-gold bg-gold/5 p-6 flex flex-col relative">
            <span className="pill bg-gold text-ink text-[9px] absolute -top-2 right-4">BEST VALUE</span>
            <div className="stamp text-gold-dark text-[9px]">ANNUAL</div>
            <div className="display text-4xl mt-2">£{annual.toFixed(0)}<span className="text-base text-mute">/yr</span></div>
            <p className="text-xs text-mute mt-1">Save £{(monthly * 12 - annual).toFixed(2)} vs monthly.</p>
            <button onClick={() => subscribe('annual')} disabled={busy !== null || data?.configured === false} className="btn btn-gold w-full mt-4">
              {busy === 'annual' ? 'Opening checkout…' : 'Choose annual'}
            </button>
            {souqliCreditGBP >= annual && (
              <button onClick={() => subscribe('annual', true)} disabled={busy !== null} className="text-[12px] text-sage-dark hover:underline mt-2">
                Pay with your £{souqliCreditGBP.toFixed(2)} Souqli Credit instead
              </button>
            )}
          </div>
          {data?.configured === false && (
            <p className="sm:col-span-2 text-[11px] text-mute text-center">Membership payments turn on once Stripe is configured.</p>
          )}
        </div>
      )}

      {/* Perks */}
      <div className="rounded-luxe border border-warmline bg-white p-6">
        <h2 className="font-semibold text-ink text-sm mb-4">{isActive ? 'Your member benefits' : 'What you get with membership'}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {perks.map((p) => (
            <div key={p.label} className="flex items-start gap-3">
              <span className="text-xl shrink-0">{p.icon}</span>
              <div>
                <p className="text-sm font-medium text-ink">{p.label}</p>
                <p className="text-xs text-mute">{p.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel modal */}
      {showCancel && (
        <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center z-50 px-4" onClick={() => setShowCancel(false)}>
          <div className="bg-white rounded-luxe p-8 max-w-[420px] w-full max-h-[90vh] overflow-y-auto shadow-frame" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-xl text-ink mb-2">Cancel membership?</h3>
            <p className="text-mute text-sm mb-6">You'll keep your benefits until {expires ? expires.toLocaleDateString() : 'the end of the period'}. After that your member perks stop.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowCancel(false)} className="btn btn-outline flex-1 text-sm py-2.5">Keep membership</button>
              <button onClick={cancel} disabled={busy === 'cancel'} className="btn flex-1 text-sm py-2.5 bg-bad text-cream hover:opacity-90">
                {busy === 'cancel' ? 'Cancelling…' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
